import { Button, TextField } from '@nike/eds'
import { useSearchToteUnitsQuery } from 'api/tote'
import { TableView } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { usePaginatedData } from 'components/tableview/usePaginatedData'
import { useTableState } from 'components/tableview/useTableState'
import { useMemo, useState } from 'react'
import { UnitStatus } from 'types/RASN'
import { type ToteSearchResult } from 'types/Tote'
import { getEnumValues } from 'utils'

const FILTER_INPUT_DELAY_MS = 500
const DEFAULT_SORT = [{ id: 'modified', desc: true }]

const ToteResults = ({ toteId }: { toteId: string }) => {
  const tableState = useTableState(DEFAULT_SORT)

  const { data, loading } = usePaginatedData<ToteSearchResult>(
    useSearchToteUnitsQuery,
    tableState.pagination,
    tableState.columnFilters,
    tableState.sorting,
    { toteId }
  )

  const columns = useMemo(() => [
    { id: 'rasn', header: 'RASN', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'goodsHolder', header: 'GoodsHolder', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'sku', header: 'SKU', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'quantity', header: 'Qty', type: ColumnType.NUMBER, columnWidth: 4 },
    { id: 'grade', header: 'Grade', type: ColumnType.TEXT, columnWidth: 4 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(UnitStatus), columnWidth: 8 },
    { id: 'station', header: 'Station', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'athlete', header: 'Athlete', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'modified', header: 'Modified', type: ColumnType.DATE, columnWidth: 12 },
    { id: 'tote', header: 'Tote', type: ColumnType.TEXT, columnWidth: 14 }
  ], [])

  return (
    <>
      <div className="eds-type--body-2 mb-4 py-2 px-1 bg-gray-100">
        Tote: {toteId} &nbsp;| &nbsp;Total Units: {data?.totalUnits ?? 0}
      </div>

      <TableView
        columns={columns}
        data={data?.results ?? []}
        rowCount={data?.totalCount ?? 0}
        tableState={tableState}
        textFilterDelay={FILTER_INPUT_DELAY_MS}
        loading={loading}
        defaultSorting={DEFAULT_SORT}
      />
    </>
  )
}

export const ToteOverview = () => {
  const [toteInput, setToteInput] = useState('')
  const [activeToteId, setActiveToteId] = useState<string | null>(null)

  const handleSearch = () => {
    const trimmed = toteInput.trim()
    if (trimmed) {
      setActiveToteId(trimmed)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div className="m-4">
      <div className="flex items-end gap-4 mb-4">
        <span className="eds-type--body-2 whitespace-nowrap pb-2">Tote ID:</span>
        <TextField
          id="tote-search-input"
          label=""
          hideLabel
          placeholder="Enter tote barcode"
          value={toteInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setToteInput(e.target.value) }}
          onKeyDown={handleKeyDown}
          style={{ width: '200px', height: '40px' }}
        />
        <Button onClick={handleSearch} size="small">Search</Button>
      </div>

      {activeToteId && <ToteResults key={activeToteId} toteId={activeToteId} />}
    </div>
  )
}
